"use client";

import { useT } from "@/hooks/useT";
import { type SmartActivityRow } from "@/hooks/useSmartActivityMap";
import { formatCompactUSD } from "../helpers/format-money";

// Predexon outage: blank only the SMART$/ROI cells under the flag. The 24h /
// total volume / liquidity cells come from the market prop (not Predexon) and
// stay untouched.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

interface NestedMarket {
  conditionId?: string;
  volume?: number;
  volume24hr?: number;
}

interface MarketsListNestedTailCellsProps {
  market: NestedMarket;
  /** Smart-money row pre-resolved by the parent (parent already calls getSmartRow once). */
  smartRow: SmartActivityRow | undefined | null;
  effectiveSourceFilter: string;
}

/**
 * The trailing 4-6 cells of a nested sub-market `<tr>` in the markets
 * list view: Smart Money (compact, count only — no volume), Smart ROI,
 * 24h Volume, Liquidity (PM-only spacer, always "N/A"), Total Volume
 * (muted), and the bookmark column spacer.
 *
 * Differs from the main row in three ways:
 *   - Padding `py-3` (main uses `py-2.5`)
 *   - Empty cells render "N/A" inline rather than collapsing to nothing
 *     (preserves the column rhythm — sub-markets often have no volume yet)
 *   - Total Volume is one font size smaller and muted to de-emphasise the
 *     row vs the parent
 *
 * Returns a Fragment so it slots into the parent `<tr>` next to the
 * Chart cell.
 */
export function MarketsListNestedTailCells({
  market,
  smartRow,
  effectiveSourceFilter,
}: MarketsListNestedTailCellsProps) {
  const t = useT();
  const isPM = effectiveSourceFilter === "polymarket";
  const tooltipEarly = t("tooltip.smartMoneyEarly");

  const vol24 = (market as any).volume24hr;
  const volTotal = (market as any).volume;

  // Smart Money + Smart ROI — PM-only, gated by the caller AND by the
  // wallet-count threshold here. Returned as a small fragment.
  const renderSmartCells = () => {
    // Predexon outage: render NO smart cells — the SMART$/ROI <col> and <th>
    // are dropped under the same flag, so the sub-market row stays aligned with
    // two fewer columns (the 24h/total/liquidity cells below are unaffected).
    if (HIDE_PREDEXON) return null;
    if (!smartRow || smartRow.smartWalletCount < 5) {
      return (
        <>
          <td className="px-4 py-3" title={tooltipEarly}>
            <div className="flex items-center justify-center gap-1.5">
              <div className="relative w-14 h-1 rounded-[5px] bg-gray-200 dark:bg-white/10 overflow-hidden" />
              <span className="text-[10px] font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
            </div>
          </td>
          <td className="px-4 py-3 text-right" title={tooltipEarly}>
            <span className="text-[12px] font-inter-tight font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
          </td>
        </>
      );
    }
    const yesPct = Math.round(smartRow.netBuyersPct * 100);
    const isYes = yesPct >= 50;
    const dominantPct = isYes ? yesPct : 100 - yesPct;
    const roi = smartRow.avgSmartRoi * 100;
    const up = roi >= 0;
    return (
      <>
        <td className="px-4 py-3" title={`${smartRow.smartWalletCount} smart wallets · ${dominantPct}% net ${isYes ? "YES" : "NO"}`}>
          <div className="flex items-center justify-center gap-1.5">
            <div className="relative w-14 h-1 rounded-[5px] bg-gray-200 dark:bg-white/10 overflow-hidden">
              <div className={`absolute inset-y-0 left-0 ${isYes ? "bg-emerald-500" : "bg-red-500"}`} style={{ width: `${dominantPct}%` }} />
            </div>
            <span className="text-[10px] font-semibold tabular-nums text-gray-600 dark:text-gray-300">{smartRow.smartWalletCount}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <span className={`text-[12px] font-inter-tight font-semibold tabular-nums ${up ? "text-emerald-500" : "text-red-500"}`}>
            {up ? "+" : ""}{roi.toFixed(1)}%
          </span>
        </td>
      </>
    );
  };

  return (
    <>
      {isPM && renderSmartCells()}

      {/* 24h Volume */}
      <td className="px-4 py-3 text-right">
        {vol24 ? (
          <span className="text-[12px] font-inter-tight font-semibold tabular-nums text-gray-700 dark:text-gray-300">
            {formatCompactUSD(vol24, 1)}
          </span>
        ) : (
          <span className="text-[12px] font-inter-tight font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
        )}
      </td>

      {/* Liquidity — PM-only spacer. Nested markets don't expose
          per-market liquidity, so the cell is always "N/A" but
          present to keep the column count aligned with the header. */}
      {isPM && (
        <td className="px-4 py-3 text-right">
          <span className="text-[12px] font-inter-tight font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
        </td>
      )}

      {/* Total Volume — muted, one size smaller than the main row. */}
      <td className="px-4 py-3 text-right">
        {volTotal ? (
          <span className="text-[11px] font-inter-tight tabular-nums text-gray-400 dark:text-gray-500">
            {formatCompactUSD(volTotal, 1)}
          </span>
        ) : (
          <span className="text-[11px] font-inter-tight tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
        )}
      </td>

      {/* Bookmark spacer for nested rows */}
      <td className="px-1 py-3" />
    </>
  );
}
